import { useState } from "react";
import { DocumentTypeDto, documentTypeFormData, DocumentTypeKeyId } from "../types/documentType";
import { documentTypeService } from "../services/documentTypeService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const useDocumentTypes = () => {
  // 1. Το GET γίνεται αυτόματα με το useApi, cache και signal
  const {
    data: documentTypes = [],
    loading: isFetching,
    error: getError,
    refetch: fetchDocumentTypes,
    invalidateCache,
  } = useApi<DocumentTypeDto[]>("document-types", (signal) => documentTypeService.getAll({}, signal));

  // 2. Το CREATE τυλίγεται στο καθαρό useMutation
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newDocumentType: documentTypeFormData) => documentTypeService.create(newDocumentType));

  // 3. Το UPDATE τυλίγεται στο καθαρό useMutation
  const {
    execute: updateExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(({ id, data }: { id: number | string; data: DocumentTypeDto | DocumentTypeFormData }) =>
    documentTypeService.update(id, data)
  );

  // 4. Το DELETE τυλίγεται στο καθαρό useMutation
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => documentTypeService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "success",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createDocumentType = async (newDocumentType: DocumentTypeFormData): Promise<void> => {
    try {
      await createExecution(newDocumentType);
      
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Ο τύπος εγγράφου αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      
      fetchDocumentTypes(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  // --- Η ΣΥΝΑΡΤΗΣΗ UPDATE ΠΟΥ ΕΛΕΙΠΕ ---
  const updateDocumentType = async (id: number | string, data: DocumentTypeDto | DocumentTypeFormData): Promise<void> => {
    try {
      await updateExecution({ id, data });
      
      // Καθαρισμός cache για να φέρει τα ενημερωμένα δεδομένα
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Ο τύπος εγγράφου ενημερώθηκε επιτυχώς!",
        type: "success",
      });
      
      fetchDocumentTypes(true); // Άμεση ανανέωση λίστας
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deleteDocumentTypes = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      
      fetchDocumentTypes(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    documentTypes,
    isLoading: isFetching || isCreating || isUpdating || isDeleting,
    error: getError || createError || updateError || deleteError,
    fetchDocumentTypes: () => fetchDocumentTypes(true),
    createDocumentType,
    updateDocumentType,
    deleteDocumentTypes,
    modalState,
    closeModal,
  };
};